import { Link } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories } from "@/mocks/videos";

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 多多视频",
    description:
      "了解多多视频的平台定位、服务内容与联系方式。多多视频提供高清影视资源分享与精彩视频播放，涵盖电影、电视剧与短视频。",
    keywords: "多多视频,关于我们,影视平台,高清影视,视频资源",
  });

  const services = [
    {
      icon: "ri-film-line",
      title: "海量影视资源",
      desc: "覆盖电影、电视剧、短视频三大内容板块，持续更新热门与高分作品，满足不同观看偏好。",
    },
    {
      icon: "ri-hd-line",
      title: "高清画质体验",
      desc: "专注于高清画质内容呈现，让每一帧画面都清晰细腻，带来更沉浸的观看感受。",
    },
    {
      icon: "ri-search-eye-line",
      title: "便捷发现",
      desc: "提供分类浏览、关键词搜索与地区年份筛选，帮助你快速找到想看的影视内容。",
    },
    {
      icon: "ri-thumb-up-line",
      title: "智能推荐",
      desc: "根据内容类型与热度进行相关推荐，让好作品主动来到你面前。",
    },
  ];

  const usage = [
    "本站当前展示的影视内容均为演示数据，仅用于产品功能与界面展示。",
    "播放按钮为前端演示效果，实际播放功能将在后台资源接入后开放。",
    "搜索、分类筛选与排序均为前端演示交互，结果仅供参考。",
    "请勿将本站演示内容用于任何商业用途，版权归原作者所有。",
  ];

  return (
    <div className="min-h-screen bg-background-950">
      <SiteNavbar />
      <main>
        <section className="border-b border-background-800 bg-background-900">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-20">
            <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-secondary-400 md:text-sm">
              <Link to="/" className="hover:text-accent-400 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-base" />
              <span className="text-foreground-200">关于我们</span>
            </nav>
            <h1 className="mt-5 font-heading text-3xl font-extrabold text-foreground-50 md:text-5xl">
              关于多多视频
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-300 md:text-lg">
              多多视频是一家专注于高清影视资源分享与精彩视频播放的平台，覆盖电影、电视剧及短视频内容。我们致力于为影视爱好者提供丰富的内容、清晰的画质与便捷的浏览体验，帮助每一位用户快速发现并欣赏自己喜欢的作品。
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50 md:text-2xl">
            <i className="ri-service-line text-accent-400" />
            我们提供什么
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
            {services.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-800 bg-background-900 p-5 transition-colors hover:border-accent-500/60 md:p-6"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500/15 text-primary-300">
                  <i className={`${item.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-50">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 pb-12 md:px-6 md:pb-16">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-lg border border-background-800 bg-background-900 p-6 lg:col-span-2 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
                <i className="ri-book-open-line text-accent-400" />
                使用须知
              </h2>
              <ul className="mt-5 flex flex-col gap-3">
                {usage.map((text) => (
                  <li key={text} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-300">
                    <i className="ri-checkbox-circle-line mt-0.5 text-lg text-accent-400" />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-800 bg-background-900 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
                <i className="ri-customer-service-2-line text-accent-400" />
                联系我们
              </h2>
              <ul className="mt-5 flex flex-col gap-4 text-sm">
                <li className="flex items-center gap-3 text-foreground-300">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-800 text-accent-400">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <span>反馈邮箱：support@duoduovideo.com</span>
                </li>
                <li className="flex items-center gap-3 text-foreground-300">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-800 text-accent-400">
                    <i className="ri-wechat-line text-lg" />
                  </span>
                  <span>微信公众号：多多视频</span>
                </li>
                <li className="flex items-center gap-3 text-foreground-300">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-800 text-accent-400">
                    <i className="ri-time-line text-lg" />
                  </span>
                  <span>服务时间：周一至周五 9:00 - 18:00</span>
                </li>
              </ul>
              <p className="mt-5 border-t border-background-800 pt-4 text-xs leading-relaxed text-secondary-400">
                如对内容、版权或合作有任何疑问，欢迎通过以上方式与我们取得联系，我们会尽快回复。
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1280px] px-4 pb-16 md:px-6 md:pb-20">
          <div className="rounded-lg border border-background-800 bg-background-900 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
              <i className="ri-compass-3-line text-accent-400" />
              浏览更多频道
            </h2>
            <div className="mt-5 flex flex-wrap gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-4 py-2.5 text-sm font-medium text-foreground-200 transition-colors hover:border-accent-400 hover:text-accent-400 cursor-pointer"
                >
                  <i className={`${cat.icon} text-lg`} />
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}